/**
 * PostDetailBody 컴포넌트
 * @description 글 상세의 본문 조립 — 매거진 조판. 넓은 커버 → (곁글 | 표제 · 리드 · 본문 · CTA).
 *   리드는 본문 첫 문단을 떼어 쓴다(`splitLede`).
 */
import { memo } from "react";

import { postDetailStyles } from "./PostDetail.styles";
import type { PostDetailBodyProps } from "./PostDetail.types";
import PostDetailAppCta from "./PostDetailAppCta";
import PostDetailHeader from "./PostDetailHeader";
import PostDetailHeroMedia from "./PostDetailHeroMedia";
import PostDetailMeta from "./PostDetailMeta";
import PostDetailRichText from "./PostDetailRichText";

import { readingMinutes } from "../../utils/readingTime";
import { splitLede } from "../../utils/splitLede";
import { toBlogLanguage } from "@/shared/config";
import { Reveal, REVEAL_STAGGER, Section } from "@/shared/ui";

/** 읽기 진행 띠(`ReadingProgress`)가 재는 대상 — 본문 article 의 id */
export const POST_ARTICLE_ID = "post-article";

const PostDetailBody = memo<PostDetailBodyProps>(({ post }) => {
  /* 제목 없는 글은 본문이 표제 자리에 서므로 리드를 떼지 않는다 */
  const { lede, body } = post.title.trim()
    ? splitLede(post.content)
    : { body: post.content };
  /* 아티클은 서버가 읽는 시간을 주고, 앱 글은 비어 온다 — 그때 본문에서 센다 */
  const minutes = post.readingMinutes ?? readingMinutes(post.content);

  return (
    <Section padding="lg">
      <article
        id={POST_ARTICLE_ID}
        lang={toBlogLanguage(post.language)}
        className={postDetailStyles.article}
      >
        <Reveal>
          <PostDetailHeroMedia post={post} />
        </Reveal>
        <div className={postDetailStyles.layout}>
          <Reveal delay={REVEAL_STAGGER} className={postDetailStyles.metaSlot}>
            <PostDetailMeta post={post} readingMinutes={minutes} />
          </Reveal>
          <div className={postDetailStyles.column}>
            <PostDetailHeader
              post={post}
              lede={lede}
              readingMinutes={minutes}
            />
            <PostDetailRichText content={body} />
            <Reveal>
              <PostDetailAppCta post={post} />
            </Reveal>
          </div>
        </div>
      </article>
    </Section>
  );
});

PostDetailBody.displayName = "PostDetailBody";

export default PostDetailBody;
